import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductForm from './ProductForm';
import ProductReportView from './ProductReportView';
import ProductDetailReportView from './ProductDetailReportView';
import ProductSummaryReportView from './ProductSummaryReportView';
import { getCurrentSchedulePrice, isManualPriceRow, ManualPriceIcon, getDerivedRrp, useRrpSettings } from './productReportShared';
import { GST_LABEL } from './gst';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import ReportLauncher from '../../../shared/ReportLauncher';
import ReportOverlay from '../../../shared/ReportOverlay';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../../shared/pagination';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import SavedFilterTabs from '../../../shared/SavedFilterTabs';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import ColumnPicker from '../../../shared/ColumnPicker';
import useTabColumns from '../../../shared/useTabColumns';
import { AvailabilityPill, AvailabilityAsEntered, CategoryPill } from '../../../shared/Pill';
import { ProductThumb } from '../../../shared/ProductThumb';
import ProgressDot from '../../../shared/ProgressDot';
import FilterBanner from '../../../shared/FilterBanner';
import { useBulkSelection, BulkSelectBar, SelectPageCheckbox, SelectRowCheckbox } from '../../../shared/BulkSelect';
import BulkEditDialog from '../../../shared/BulkEditDialog';
import { Filter as FilterIcon } from 'lucide-react';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import ParagraphText from '../../../shared/ParagraphText';
import { formatMoney } from '../../../shared/numberFormat';
import { useScreenAccess } from '../../../shared/PermissionsContext';
import { QuickBooksItemStatusPill } from '../../../shared/QuickBooksItemPicker';
import brandLabel from '../../../shared/brandLabel';
import { askConfirm } from '../../../shared/AppDialogs';

const gstLabelStyle = { fontSize: 13, fontWeight: 400, color: '#888' };

// Human-readable name for each sortable column, for the report subtitle's
// "Sort: ..." summary — keyed by the same column name handleSort/the API
// use. 'rrp' label switches with Track RRP (see buildFilterSummary).
const SORT_LABELS = { sku: 'SKU', name: 'Name' };
// State values (hidden/backorder/available) and badge values (available_
// soon/backorder_now/new) share one filter param/dropdown on the backend
// (productController.js's `availability`) since the two value sets don't
// overlap — labelled together here for the same reason.
const AVAILABILITY_LABELS = {
  hidden: 'Hidden',
  backorder: 'Backorder',
  preorder: 'Pre-order',
  available: 'Available',
  available_soon: 'From Date (not yet orderable)',
  backorder_now: 'Backorder',
  preorder_now: 'Pre-order',
  new: 'New',
};

// Identifies this screen's saved filters (SavedFilterTabs) from any other
// screen's — same shared saved_filters table/pattern as Price Adjustments
// (Page 3-1) and Pricelists. Search stays outside — free-text/unbounded,
// not saveable, same split those screens use.
const FILTER_SCREEN_KEY = 'products';
const STATUS_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];
const DEFAULT_FILTERS = { status: 'all', category: '', product_type: '', brand: '', price_group: '', uom: '', availability: '', product_id: '', quickbooks: '' };
// QuickBooks match filter (issue #265).
const QUICKBOOKS_LABELS = { matched: 'Matched', unmatched: 'Unmatched', needs_attention: 'Needs attention' };
// Optional list-table columns (Columns picker, persisted per-user via
// /api/column-preferences) — Image/SKU/Name/actions stay always-visible
// (SYSTEM_COLUMNS) since they're identity columns, not display preference.
const OPTIONAL_COLUMNS = [
  { key: 'description', label: 'Description' },
  { key: 'availability', label: 'Availability' },
  { key: 'availability_badge', label: 'Availability Badge' },
  { key: 'category', label: 'Category' },
  { key: 'brand', label: 'Brand' },
  { key: 'price_group', label: 'Price Group' },
  { key: 'product_type', label: 'Product Type' },
  { key: 'base_uom', label: 'Base UoM' },
  { key: 'cost', label: 'Cost' },
  { key: 'rrp', label: 'RRP / Retail Price' },
  { key: 'weights_dims', label: 'Weights & Dims' },
  { key: 'quickbooks', label: 'QuickBooks Item' },
];
const SYSTEM_COLUMNS = ['Image', 'SKU', 'Name', 'Actions'];
// The everyday job (ui-architecture.md Column Picker): what it is, whether
// it can be sold, and what it costs and sells for. Everything else is one
// tick away in Columns — Availability Badge to check what a customer will
// see, Weights & Dims for shipping work (a good saved tab's own column set).
const DEFAULT_VISIBLE_COLUMNS = ['availability', 'category', 'brand', 'cost', 'rrp'];

// Weight/Length/Width/Height only ever apply to a UoM row that's buyable or
// sellable (issue #100) — those are the only cells that count toward
// completeness here. none/partial/full drives the same ProgressDot used for
// Sales Orders' Picked/Packed/Shipped columns, not a raw value — a product
// can carry several UoM rows, each with its own weight/dims, so there's no
// single number to show in one column.
function weightsDimsState(item) {
  const rows = (item.uoms || []).filter((r) => r.can_buy || r.can_sell);
  const totalCells = rows.length * 4;
  if (totalCells === 0) return { state: 'empty', title: 'No buyable/sellable Units of Measure' };
  const filledCells = rows.reduce((sum, r) => (
    sum + ['weight', 'length', 'width', 'height'].filter((f) => r[f] !== null && r[f] !== undefined).length
  ), 0);
  if (filledCells === 0) return { state: 'empty', title: 'No Weights & Dimensions entered' };
  if (filledCells === totalCells) return { state: 'full', title: 'Weights & Dimensions fully entered' };
  return { state: 'partial', title: 'Weights & Dimensions part entered' };
}
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };

// Exact-match Product picker for the filter drawer — same search-as-you-type
// pattern as PriceLookupPage.js's Product field, but resolving to `product_id`
// (an exact `where.id`, productController.js) rather than the free-text
// `search` param, which only ever does a partial name/SKU match. A dedicated
// component (not inlined in renderDrawerFields) because that function is
// called from two places in one render (the ad hoc drawer + SavedFilterTabs),
// which would violate the rules of hooks if the search/dropdown state lived
// there directly.
function ProductExactFilterField({ productId, onChange }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const skipNextSearch = useRef(true);

  // Reflect an externally-set productId (drawer reopened, a saved filter
  // applied) as its SKU/name label rather than leaving the box blank.
  useEffect(() => {
    if (!productId) { setSearch(''); return; }
    skipNextSearch.current = true;
    axios.get(`/api/products/${productId}`)
      .then((res) => { const p = res.data.data; setSearch(p ? `${p.sku} — ${p.name}` : ''); })
      .catch(() => {});
  }, [productId]);

  useEffect(() => {
    if (skipNextSearch.current) { skipNextSearch.current = false; return; }
    if (!search.trim()) { setResults([]); setShowResults(false); return; }
    const timer = setTimeout(() => {
      axios.get('/api/products', { params: { search, status: 'active', limit: 10 } })
        .then((res) => { setResults(res.data.data || []); setShowResults(true); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSelect = (p) => {
    skipNextSearch.current = true;
    onChange(String(p.id));
    setSearch(`${p.sku} — ${p.name}`);
    setShowResults(false);
  };

  const handleClear = () => {
    skipNextSearch.current = true;
    onChange('');
    setSearch('');
    setShowResults(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <label style={FIELD_LABEL_STYLE}>Product (exact match)</label><br />
      <input
        className="styled-input"
        placeholder="Search name or SKU..."
        value={search}
        onChange={(e) => { onChange(''); setSearch(e.target.value); }}
        onFocus={() => { if (results.length) setShowResults(true); }}
        onBlur={() => setTimeout(() => setShowResults(false), 150)}
        style={FIELD_INPUT_STYLE}
      />
      {productId && (
        <button type="button" onClick={handleClear} title="Clear product" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
      )}
      {showResults && results.length > 0 && (
        <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: '100%', margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {results.map((p) => (
            <li
              key={p.id}
              onMouseDown={() => handleSelect(p)}
              style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {p.sku} — {p.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatPrice(price) {
  return formatMoney(price);
}

function renderCostPrice(item) {
  if (item.supplier && item.cost_price_foreign !== null && item.cost_price_foreign !== undefined) {
    const foreign = Number(item.cost_price_foreign);
    const aud = foreign * Number(item.supplier.exchange_rate);
    return (
      <>
        <div>{formatMoney(foreign, { currency: item.supplier.currency_code })}</div>
        <div style={{ color: '#888', fontSize: 12 }}>{formatPrice(aud)}</div>
      </>
    );
  }
  return formatPrice(item.cost_price);
}

// Price right-aligned as normal; the UoM code sits in its own fixed-width
// slot so codes line up in a clean column regardless of how many digits the
// price has. The `*` manual-override marker (when present) mirrors the same
// flex trick and the same marker used in the Report (productReportShared.js)
// so the two stay visually consistent.
function renderPriceWithUom(price, code, isManual) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <span>{formatPrice(price)}</span>
      <span style={{ width: 18, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        {isManual && <ManualPriceIcon />}
      </span>
      <span style={{ width: 34, flexShrink: 0, textAlign: 'left', marginLeft: 4, color: '#888', fontSize: 12 }}>{code || ''}</span>
    </div>
  );
}

// Only rendered while a UoM filter is active, so every row is guaranteed to
// have a matching products_uom row (the backend's `uom` filter enforces it).
function renderFilteredUomPrice(item, uomId) {
  const row = (item.uoms || []).find((r) => String(r.uom_id) === String(uomId));
  if (!row) return null;
  return renderPriceWithUom(getCurrentSchedulePrice(row.price_schedule), row.uom?.code, isManualPriceRow(row, item));
}

// Availability column: the Product's settings as entered (staff data entry /
// verification). The customer-facing pill is the optional Availability Badge
// column below.
function renderAvailabilityAsEntered(item) {
  return (
    <AvailabilityAsEntered
      date={item.availability_date}
      override={item.availability_override}
      hidePreBadge={item.hide_pre_availability_badge}
      hideNewBadge={item.hide_new_badge}
    />
  );
}

// availability_state/availability_badge/availability_badge_color are
// computed server-side (see availabilityResolver.js) — a single evolving
// badge, so at most one pill ever shows per row.
function renderAvailabilityBadges(item) {
  return (
    <AvailabilityPill
      badge={item.availability_badge}
      date={item.availability_date}
      color={item.availability_badge_color}
    />
  );
}

function ProductTable({ onNavigate }) {
  const { canCreate, canUpdate } = useScreenAccess();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [reportItem, setReportItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  // Lazy-init so a "?search=<term>" deep link (e.g. the top search bar's
  // SKU shortcut) is baked into the very first fetch — see the `filters`
  // lazy-init below for why an effect-driven set would race it.
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('sku');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  // Lazy-init so a "?category=<id>", "?product_type=<id>", "?uom=<id>" or
  // "?brand=<id>" deep link (the In Use drill-down from Product Categories /
  // Product Types / UoMs / Brands) is baked into the very first fetch — setting it via an
  // effect after mount would fire a second, filtered request racing the
  // initial unfiltered one, with no guarantee the filtered response wins.
  // A dashboard tile can open this list already filtered (initialFilters).
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    const categoryId = searchParams.get('category');
    const productTypeId = searchParams.get('product_type');
    const uomId = searchParams.get('uom');
    const brandId = searchParams.get('brand');
    return { ...DEFAULT_FILTERS, ...(location.state?.initialFilters || {}), ...(categoryId ? { category: categoryId } : {}), ...(productTypeId ? { product_type: productTypeId } : {}), ...(uomId ? { uom: uomId } : {}), ...(brandId ? { brand: brandId } : {}) };
  });
  const { status, category, product_type, brand, price_group, uom, availability, product_id, quickbooks } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };
  // Default tab is unfiltered (Status "all"), so the badge counts against
  // DEFAULT_FILTERS — see ui-architecture.md "Single-Row Filters".
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  // Ad hoc "Filters" panel — draft is null while closed, same pattern as
  // CommercialEntityTable.js's Filters button.
  const [filterDraft, setFilterDraft] = useState(null);
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [priceGroups, setPriceGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportMode, setReportMode] = useState(null); // 'detail' | 'summary' | null
  const [reportRows, setReportRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportFilterSummary, setReportFilterSummary] = useState('');
  const [primaryImages, setPrimaryImages] = useState({});
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const { trackRrp, defaultTaxRate, anchorName } = useRrpSettings();
  const optionalColumns = useMemo(
    () => OPTIONAL_COLUMNS.map((c) => (c.key === 'rrp' ? { ...c, label: `RRP / ${anchorName} Price` } : c)),
    [anchorName],
  );
  const tabColumns = useTabColumns(optionalColumns, SYSTEM_COLUMNS);

  useDefaultPageSize(setLimit);

  // The list's filter params, minus page/limit/sort — what "Select all N
  // matching" sends to the bulk endpoint, and the key that clears the
  // selection whenever the filter or search changes.
  const listFilterParams = { search, status, category, product_type, brand, price_group, uom, availability, product_id, quickbooks };
  const selection = useBulkSelection({
    pageIds: rows.map((r) => r.id),
    total: pagination.total,
    resetKey: JSON.stringify(listFilterParams),
  });

  // Marketing-primary thumbnail per product, for the listing's Image column
  // — same bulk lookup PricelistExtendedReport.js uses, scoped to just the
  // current page's rows rather than every product in the tenant.
  const productIdsKey = rows.map((r) => r.id).join(',');
  useEffect(() => {
    if (!productIdsKey) {
      setPrimaryImages({});
      return;
    }
    axios.get('/api/media-images/primary-for-products', { params: { product_ids: productIdsKey } })
      .then((res) => setPrimaryImages(res.data.data || {}))
      .catch(() => setPrimaryImages({}));
  }, [productIdsKey]);

  // The page doesn't scroll the window — the scrollable box is the app
  // shell's .main-content div — so window.scrollTo() is a no-op here.
  // scrollIntoView() finds whichever ancestor actually scrolls. It must
  // also wait until the form has actually mounted; firing synchronously in
  // the click handler races the browser's scroll anchoring, which then
  // re-adjusts to compensate for the form's height appearing above it.
  const topRef = useRef(null);
  useEffect(() => {
    if (!showForm) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });
  }, [showForm, editItem]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, sortBy, sortOrder, page, limit, status, category, product_type, brand, price_group, uom, availability, product_id, quickbooks]);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
    fetchUoms();
    fetchProductTypes();
    fetchBrands();
    fetchPriceGroups();
  }, []);

  const fetchPriceGroups = async () => {
    try {
      const res = await axios.get('/api/product-price-groups');
      setPriceGroups(res.data.data || []);
    } catch (err) {
      setPriceGroups([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/api/product-categories');
      setCategories(res.data.data || []);
    } catch (err) {
      setCategories([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get('/api/supplier-entities', { params: { status: 'active' } });
      setSuppliers(res.data.data || []);
    } catch (err) {
      setSuppliers([]);
    }
  };

  const fetchUoms = async () => {
    try {
      const res = await axios.get('/api/uoms', { params: { status: 'active' } });
      setUoms(res.data.data || []);
    } catch (err) {
      setUoms([]);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const res = await axios.get('/api/product-types', { params: { status: 'active' } });
      setProductTypes(res.data.data || []);
    } catch (err) {
      setProductTypes([]);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get('/api/brands', { params: { status: 'active' } });
      setBrands(res.data.data || []);
    } catch (err) {
      setBrands([]);
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { search, sortBy, sortOrder, page, limit, status, category, product_type, brand, price_group, uom, availability, product_id, quickbooks };
      const res = await axios.get('/api/products', { params });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
      setPagination({ page, limit, total: 0, pages: 1 });
    }
    setLoading(false);
  };

  // Human-readable summary of the current filters and sort, stamped onto
  // the Detail/Summary Report subtitle (pat-architecture-reports-ui-standard.md)
  // so the report stays self-documenting once printed/exported and read
  // later without the app open.
  const buildFilterSummary = () => {
    const parts = [`Status: ${STATUS_OPTIONS.find((opt) => opt.key === status)?.label || 'All'}`];
    if (search) parts.push(`Search "${search}"`);
    if (category) {
      const cat = categories.find((c) => String(c.id) === String(category));
      parts.push(`Category: ${cat ? cat.name : category}`);
    }
    if (product_type) {
      const pt = productTypes.find((t) => String(t.id) === String(product_type));
      parts.push(`Product Type: ${pt ? pt.name : product_type}`);
    }
    if (brand) {
      const b = brands.find((x) => String(x.id) === String(brand));
      parts.push(`Brand: ${brand === 'none' ? 'No brand' : (b ? b.name : brand)}`);
    }
    if (price_group) {
      const g = priceGroups.find((x) => String(x.id) === String(price_group));
      parts.push(`Price Group: ${price_group === 'none' ? 'No price group' : (g ? g.name : price_group)}`);
    }
    if (uom) {
      const u = uoms.find((x) => String(x.id) === String(uom));
      parts.push(`UoM: ${u ? `${u.code} — ${u.name}` : uom}`);
    }
    if (availability) parts.push(`Availability: ${AVAILABILITY_LABELS[availability] || availability}`);
    if (quickbooks) parts.push(`QuickBooks: ${QUICKBOOKS_LABELS[quickbooks] || quickbooks}`);
    if (product_id) {
      const p = rows.find((r) => String(r.id) === String(product_id));
      parts.push(`Product: ${p ? `${p.sku} — ${p.name}` : product_id}`);
    }
    const sortLabel = sortBy === 'rrp' ? anchorName : (SORT_LABELS[sortBy] || sortBy);
    const sortSummary = `Sort: ${sortLabel} (${sortOrder === 'ASC' ? 'Ascending' : 'Descending'})`;
    return `${parts.join(', ')} · ${sortSummary}`;
  };

  // Detail/Summary Report (story #78) — every product matching the current
  // filters, ignoring the table's page size, in the table's current sort.
  const openReport = async (mode) => {
    setShowForm(false);
    setReportItem(null);
    setReportMode(mode);
    setReportFilterSummary(buildFilterSummary());
    setReportLoading(true);
    try {
      const params = { search, sortBy, sortOrder, status, category, product_type, brand, price_group, uom, availability, product_id, quickbooks, all: true };
      const res = await axios.get('/api/products', { params });
      setReportRows(res.data.data || []);
    } catch (err) {
      setReportRows([]);
    }
    setReportLoading(false);
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(col);
      setSortOrder('ASC');
    }
  };

  const handleAdd = () => {
    setReportItem(null);
    setReportMode(null);
    setEditItem(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setReportItem(null);
    setReportMode(null);
    setEditItem(item);
    setShowForm(true);
  };

  // Deep link support (e.g. "Edit on Product" from the UoM page):
  // /products?edit=<id> opens straight to that product's edit form.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId) return;
    axios.get(`/api/products/${editId}`)
      .then((res) => handleEdit(res.data.data))
      .catch(() => {});
    setSearchParams((params) => {
      params.delete('edit');
      return params;
    }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  // Deep link support (e.g. "New Product" from the Home dashboard):
  // /products?new=1 opens straight to the blank add form.
  useEffect(() => {
    if (!searchParams.get('new')) return;
    if (canCreate) handleAdd();
    setSearchParams((params) => {
      params.delete('new');
      return params;
    }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  // Deep link support (the "In Use" count drill-down from Product
  // Categories / Product Types / UoMs, and the top search bar's SKU
  // shortcut): /products?category=<id>, ?product_type=<id>, ?uom=<id>, ?brand=<id>, or
  // ?search=<term> pre-filters the list (seeded into `filters`/`search`'s
  // initial state above, not here — see those comments for why). This just
  // scrubs whichever param was consumed once mount has read it.
  useEffect(() => {
    if (!searchParams.get('category') && !searchParams.get('product_type') && !searchParams.get('uom') && !searchParams.get('brand') && !searchParams.get('search')) return;
    setSearchParams((params) => {
      params.delete('category');
      params.delete('product_type');
      params.delete('uom');
      params.delete('brand');
      params.delete('search');
      return params;
    }, { replace: true });
    // eslint-disable-next-line
  }, [searchParams]);

  const handleReport = (item) => {
    setShowForm(false);
    setReportMode(null);
    setReportItem(item);
  };

  // Best-effort suggestion only — the (tenant_id, sku) unique index applies
  // regardless of is_active, so both statuses are checked; the real
  // authority remains addProduct's own 409 DUPLICATE_ENTRY check on save.
  const suggestDuplicateSku = async (baseSku) => {
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        axios.get('/api/products', { params: { search: baseSku, status: 'active', all: true } }),
        axios.get('/api/products', { params: { search: baseSku, status: 'inactive', all: true } }),
      ]);
      const taken = new Set(
        [...(activeRes.data.data || []), ...(inactiveRes.data.data || [])].map((p) => p.sku)
      );
      let n = 1;
      while (taken.has(`${baseSku}-${n}`)) n++;
      return `${baseSku}-${n}`.slice(0, 50);
    } catch (err) {
      return `${baseSku}-1`.slice(0, 50);
    }
  };

  // Duplicate: pre-fill the Add form from an existing product, minus its id
  // (so Save POSTs a new product) and minus its UoM/price data (so it gets a
  // fresh base UoM row with no prices, same as any new product). Supplier SKU
  // is blanked since it must be unique per supplier.
  const handleDuplicate = async (item) => {
    setReportItem(null);
    setReportMode(null);
    const suggestedSku = await suggestDuplicateSku(item.sku);
    setEditItem({ ...item, id: undefined, sku: suggestedSku, supplier_sku: '', _duplicateSourceId: item.id });
    setShowForm(true);
  };

  const handleDeactivate = async (item) => {
    if (!(await askConfirm(`Deactivate product "${item.name}"?`, { confirmLabel: 'Deactivate' }))) return;
    try {
      await axios.patch(`/api/products/${item.id}/deactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Deactivate failed');
    }
  };

  const handleReactivate = async (item) => {
    try {
      await axios.patch(`/api/products/${item.id}/reactivate`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Reactivate failed');
    }
  };

  const handleDelete = async (item) => {
    if (!(await askConfirm(`Permanently delete product "${item.name}"? This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/products/${item.id}`);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Delete failed');
    }
  };

  // Bulk Edit's Classification fields — active values only, same as the
  // Product form offers. Category/Product Type are required on a Product, so
  // they get no "(none)" choice. Availability Date's "(none)" clears it
  // (always available).
  const bulkEditFields = [
    { key: 'product_type_id', label: 'Product Type', required: true, options: productTypes.map((t) => ({ value: t.id, label: `${t.code} — ${t.name}` })) },
    { key: 'product_category_id', label: 'Category', required: true, options: categories.filter((c) => c.is_active).map((c) => ({ value: c.id, label: `${'\u00A0\u00A0\u00A0'.repeat(c.depth || 0)}${c.name}` })) },
    { key: 'brand_id', label: 'Brand', required: true, options: brands.map((b) => ({ value: b.id, label: brandLabel(b) })) },
    { key: 'product_price_group_id', label: 'Product Price Group', required: false, options: priceGroups.filter((g) => g.is_active).map((g) => ({ value: g.id, label: g.name })) },
    { key: 'availability_date', label: 'Availability Date', type: 'date', required: false },
  ];

  const handleBulkApply = async (field, value) => {
    try {
      await axios.post('/api/products/bulk-update', { field, value, ...selection.target(listFilterParams) });
      setShowBulkEdit(false);
      selection.clear();
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Bulk Edit failed');
      throw err;
    }
  };

  const handleSave = async (data) => {
    setFormLoading(true);
    try {
      if (editItem && editItem.id) {
        await axios.put(`/api/products/${editItem.id}`, data);
      } else {
        await axios.post('/api/products', data);
      }
      setShowForm(false);
      fetchRows();
      dismissError();
    } catch (err) {
      showError(err, 'Save failed');
    }
    setFormLoading(false);
  };

  const uomFilterActive = !!uom;
  const showDescription = visibleColumns.includes('description');
  const showAvailability = visibleColumns.includes('availability');
  const showAvailabilityBadge = visibleColumns.includes('availability_badge');
  const showCategory = visibleColumns.includes('category');
  const showBrand = visibleColumns.includes('brand');
  const showPriceGroup = visibleColumns.includes('price_group');
  const showProductType = visibleColumns.includes('product_type');
  const showBaseUom = visibleColumns.includes('base_uom');
  const showCost = visibleColumns.includes('cost');
  const showRrp = visibleColumns.includes('rrp');
  const showWeightsDims = visibleColumns.includes('weights_dims');
  const showQuickbooks = visibleColumns.includes('quickbooks');
  const totalCols = 5 // select, media, SKU, Name, actions
    + OPTIONAL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).length
    + (uomFilterActive ? 1 : 0);

  // The drawer's field list for SavedFilterTabs — Status/Category/UoM, the
  // same three fixed-value dimensions already visible as always-on quick
  // selects above. Search stays outside — free-text/unbounded, not
  // saveable, same split Price Adjustments/Pricelists already use.
  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.key} value={opt.key}>{opt.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Category</label><br />
        <select className="styled-input" value={draft.category} onChange={(e) => setDraftField('category', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{'\u00A0\u00A0\u00A0'.repeat(c.depth || 0)}{c.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Product Type</label><br />
        <select className="styled-input" value={draft.product_type} onChange={(e) => setDraftField('product_type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All product types</option>
          {productTypes.map((t) => (
            <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Brand</label><br />
        <select className="styled-input" value={draft.brand} onChange={(e) => setDraftField('brand', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All brands</option>
          <option value="none">No brand</option>
          {brands.map((b) => (
            <option key={b.id} value={b.id}>{brandLabel(b)}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Price Group</label><br />
        <select className="styled-input" value={draft.price_group || ''} onChange={(e) => setDraftField('price_group', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All price groups</option>
          <option value="none">No price group</option>
          {priceGroups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}{g.is_active ? '' : ' (inactive)'}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>UoM</label><br />
        <select className="styled-input" value={draft.uom} onChange={(e) => setDraftField('uom', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All UoMs</option>
          {uoms.map((u) => (
            <option key={u.id} value={u.id}>{u.code} — {u.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Availability</label><br />
        <select className="styled-input" value={draft.availability} onChange={(e) => setDraftField('availability', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All availability</option>
          <option value="hidden">Hidden</option>
          <option value="backorder">Backorder</option>
          <option value="preorder">Pre-order</option>
          <option value="available">Available</option>
          <optgroup label="Badge">
            <option value="available_soon">From Date (not yet orderable)</option>
            <option value="backorder_now">Backorder</option>
            <option value="preorder_now">Pre-order</option>
            <option value="new">New</option>
          </optgroup>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>QuickBooks</label><br />
        <select className="styled-input" value={draft.quickbooks || ''} onChange={(e) => setDraftField('quickbooks', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All</option>
          {Object.entries(QUICKBOOKS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>
      <ProductExactFilterField
        productId={draft.product_id}
        onChange={(id) => setDraftField('product_id', id)}
      />
    </>
  );

  return (
    <div ref={topRef} style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32, overflowAnchor: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Products</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {canCreate && (
            <button
              onClick={handleAdd}
              disabled={showForm}
              title={showForm ? 'Finish editing first' : undefined}
              style={{
                padding: '8px 18px',
                background: showForm ? '#9fb3c8' : '#1976d2',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 500,
                cursor: showForm ? 'not-allowed' : 'pointer',
              }}
            >
              Add Product
            </button>
          )}
          <ReportLauncher
            options={[
              { key: 'detail', label: 'Detail Report', onSelect: () => openReport('detail') },
              { key: 'summary', label: 'Summary Report', onSelect: () => openReport('summary') },
            ]}
            activeContent={
              reportMode === 'detail' ? (
                <ProductDetailReportView products={reportRows} loading={reportLoading} filterSummary={reportFilterSummary} trackRrp={trackRrp} defaultTaxRate={defaultTaxRate} anchorName={anchorName} onClose={() => setReportMode(null)} />
              ) : reportMode === 'summary' ? (
                <ProductSummaryReportView products={reportRows} loading={reportLoading} filterSummary={reportFilterSummary} trackRrp={trackRrp} defaultTaxRate={defaultTaxRate} anchorName={anchorName} onClose={() => setReportMode(null)} />
              ) : null
            }
            onCloseActive={() => setReportMode(null)}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <input
                  className="styled-input"
                  placeholder="Search name or SKU..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  style={{ width: 200 }}
                />
                {search && (
                  <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
                )}
              </div>
              <button
                type="button"
                onClick={openFilterPanel}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 12px',
                  borderRadius: 6,
                  border: '1px solid #bfc8d6',
                  background: activeFilterCount ? '#eef1f5' : '#fff',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <FilterIcon size={15} strokeWidth={2} />
                Filters
                {activeFilterCount > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              <ColumnPicker
                screenKey={FILTER_SCREEN_KEY}
                columns={optionalColumns}
                defaultVisible={DEFAULT_VISIBLE_COLUMNS}
                onChange={setVisibleColumns}
                tabColumns={tabColumns}
                systemColumns={SYSTEM_COLUMNS}
              />
            </div>

            <div style={{ margin: '10px 24px 0' }}>
              <SavedFilterTabs
                screenKey={FILTER_SCREEN_KEY}
                tabColumns={tabColumns}
                defaultFilters={DEFAULT_FILTERS}
                currentFilters={filters}
                onApply={applyFilters}
                renderFields={renderDrawerFields}
              />
            </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the product list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      {category && (
        <FilterBanner
          message={`Showing products in category "${categories.find((c) => String(c.id) === String(category))?.name || category}"`}
          onClear={() => applyFilters({ ...filters, category: DEFAULT_FILTERS.category })}
        />
      )}
      {product_type && (
        <FilterBanner
          message={`Showing products with product type "${productTypes.find((t) => String(t.id) === String(product_type))?.name || product_type}"`}
          onClear={() => applyFilters({ ...filters, product_type: DEFAULT_FILTERS.product_type })}
        />
      )}
      {brand && brand !== 'none' && (
        <FilterBanner
          message={`Showing products with brand "${brands.find((b) => String(b.id) === String(brand))?.name || brand}"`}
          onClear={() => applyFilters({ ...filters, brand: DEFAULT_FILTERS.brand })}
        />
      )}
      {uom && (
        <FilterBanner
          message={(() => {
            const u = uoms.find((x) => String(x.id) === String(uom));
            return `Showing products in UoM "${u ? `${u.code} — ${u.name}` : uom}"`;
          })()}
          onClear={() => applyFilters({ ...filters, uom: DEFAULT_FILTERS.uom })}
        />
      )}
      {product_id && (
        <FilterBanner
          message={(() => {
            const p = rows.find((r) => String(r.id) === String(product_id));
            return `Showing product "${p ? `${p.sku} — ${p.name}` : product_id}" only`;
          })()}
          onClear={() => applyFilters({ ...filters, product_id: DEFAULT_FILTERS.product_id })}
        />
      )}

      <BulkSelectBar
        selection={selection}
        noun="Products"
        actions={[{ key: 'bulk-edit', label: 'Bulk Edit', onClick: () => setShowBulkEdit(true) }]}
      />
      {showBulkEdit && (
        <BulkEditDialog
          onOpenChange={setShowBulkEdit}
          count={selection.count}
          noun="Products"
          fields={bulkEditFields}
          onApply={handleBulkApply}
        />
      )}

      {showForm && (
        <div style={{ background: '#f7f9fc', border: '1px solid #e3e7ee', padding: 20, margin: '18px 24px 0', borderRadius: 8 }}>
          <ProductForm
            key={editItem?.id ?? (editItem?._duplicateSourceId ? `dup-${editItem._duplicateSourceId}` : 'add')}
            initial={editItem}
            categories={categories}
            suppliers={suppliers}
            uoms={uoms}
            productTypes={productTypes}
            brands={brands}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
            onManageCategories={onNavigate ? () => onNavigate('inventory-product-categories') : undefined}
            onManageSuppliers={onNavigate ? () => onNavigate('suppliers') : undefined}
            onManageUoms={editItem && editItem.id ? () => navigate(`/products/${editItem.id}/uom`) : (onNavigate ? () => onNavigate('inventory-uom') : undefined)}
            onManageProductTypes={onNavigate ? () => onNavigate('inventory-product-types') : undefined}
            onManageBrands={onNavigate ? () => onNavigate('inventory-brands') : undefined}
            onQuickBooksChange={fetchRows}
          />
        </div>
      )}

      {reportItem && (
        <ReportOverlay onClose={() => setReportItem(null)}>
          <ProductReportView product={reportItem} trackRrp={trackRrp} defaultTaxRate={defaultTaxRate} anchorName={anchorName} onClose={() => setReportItem(null)} />
        </ReportOverlay>
      )}

      <div style={{ margin: '24px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '3%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: uomFilterActive ? '22%' : (showDescription ? '24%' : '34%') }} />
            {showDescription && <col style={{ width: '20%' }} />}
            {showAvailability && <col style={{ width: '11%' }} />}
            {showAvailabilityBadge && <col style={{ width: '10%' }} />}
            {showCategory && <col style={{ width: '10%' }} />}
            {showBrand && <col style={{ width: '8%' }} />}
            {showPriceGroup && <col style={{ width: '8%' }} />}
            {showProductType && <col style={{ width: '5%' }} />}
            {showBaseUom && <col style={{ width: '5%' }} />}
            {showCost && <col style={{ width: '9%' }} />}
            {uomFilterActive && <col style={{ width: '13%' }} />}
            {showRrp && <col style={{ width: '7%' }} />}
            {showWeightsDims && <col style={{ width: '6%' }} />}
            {showQuickbooks && <col style={{ width: '14%' }} />}
            <col style={{ width: '6%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 4px 12px 10px', borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}><SelectPageCheckbox selection={selection} /></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7' }}></th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('sku')}>SKU {sortBy === 'sku' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'left' }} onClick={() => handleSort('name')}>Name {sortBy === 'name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</th>
              {showDescription && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Description</th>}
              {showAvailability && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Availability</th>}
              {showAvailabilityBadge && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Availability Badge</th>}
              {showCategory && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Category</th>}
              {showBrand && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Brand</th>}
              {showPriceGroup && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Price Group</th>}
              {showProductType && (
                <th style={{ padding: '8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'center', lineHeight: 1.3 }}>
                  <div>Product</div>
                  <div>Type</div>
                </th>
              )}
              {showBaseUom && (
                <th style={{ padding: '8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'center', lineHeight: 1.3 }}>
                  <div>Base</div>
                  <div>UoM</div>
                </th>
              )}
              {showCost && (
                <th style={{ padding: '8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'right', lineHeight: 1.3 }}>
                  <div>Cost</div>
                  <div style={gstLabelStyle}>{GST_LABEL.cost}</div>
                </th>
              )}
              {uomFilterActive && (
                <th style={{ padding: '8px 56px 8px 8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'right', lineHeight: 1.3 }}>
                  <div>UoM</div>
                  <div>Price</div>
                  <div style={gstLabelStyle}>{GST_LABEL.uomPrice}</div>
                </th>
              )}
              {showRrp && (
                <th style={{ padding: '8px', fontWeight: 600, fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #b3bbc7', textAlign: 'right', lineHeight: 1.3 }} onClick={() => handleSort('rrp')}>
                  <div>{anchorName} {sortBy === 'rrp' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}</div>
                  <div style={gstLabelStyle}>{trackRrp ? 'Inc GST' : 'Ex GST'}</div>
                </th>
              )}
              {showWeightsDims && (
                <th style={{ padding: '8px', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #b3bbc7', textAlign: 'center', lineHeight: 1.3 }}>
                  <div>Weights</div>
                  <div>&amp; Dims</div>
                </th>
              )}
              {showQuickbooks && <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>QuickBooks Item</th>}
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No results</td></tr>
            ) : rows.map((item) => (
              <tr
                key={item.id}
                className="list-row"
                onClick={() => { if (!showForm && canUpdate) handleEdit(item); }}
                style={{ cursor: showForm || !canUpdate ? 'default' : 'pointer' }}
                title={showForm || !canUpdate ? undefined : 'Click to edit'}
              >
                <td className="cell-select" style={{ padding: '6px 4px 6px 10px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <SelectRowCheckbox selection={selection} id={item.id} />
                </td>
                <td className="cell-media" style={{ padding: '6px 8px' }}><ProductThumb entry={primaryImages[String(item.id)]} /></td>
                <td data-label="SKU" style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600 }}>{item.sku}</td>
                <td className="cell-title" style={{ padding: '6px 8px', fontSize: 14, lineHeight: 1.6 }}>
                  {item.name}
                </td>
                {showDescription && <td data-label="Description" style={{ padding: '6px 8px', fontSize: 13, color: '#555' }}><ParagraphText text={item.description} /></td>}
                {showAvailability && <td data-label="Availability" style={{ padding: '6px 8px', fontSize: 14 }}>{renderAvailabilityAsEntered(item)}</td>}
                {showAvailabilityBadge && <td data-label="Availability Badge" style={{ padding: '6px 8px', fontSize: 14 }}>{renderAvailabilityBadges(item)}</td>}
                {showCategory && <td data-label="Category" style={{ padding: '6px 8px', fontSize: 14 }}>{item.product_category?.name || ''}</td>}
                {showBrand && <td data-label="Brand" style={{ padding: '6px 8px', fontSize: 14 }}>{item.brand?.name || ''}</td>}
                {showPriceGroup && <td data-label="Price Group" style={{ padding: '6px 8px', fontSize: 14 }}>{item.product_price_group?.name || ''}</td>}
                {showProductType && (
                  <td data-label="Type" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 14 }}>
                    <CategoryPill value={item.product_type?.code} title={item.product_type?.name} />
                  </td>
                )}
                {showBaseUom && (
                  <td data-label="Base UoM" style={{ padding: '6px 8px', textAlign: 'center', fontSize: 14 }} title={item.base_uom?.name}>
                    {item.base_uom?.code || ''}
                  </td>
                )}
                {showCost && <td data-label="Cost" style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14, lineHeight: 1.3 }}>{renderCostPrice(item)}</td>}
                {uomFilterActive && (
                  <td data-label="UoM Price" style={{ padding: '6px 8px', fontSize: 14 }}>{renderFilteredUomPrice(item, uom)}</td>
                )}
                {showRrp && <td data-label={anchorName} style={{ padding: '6px 8px', textAlign: 'right', fontSize: 14 }}>{formatPrice(getDerivedRrp(item, trackRrp, defaultTaxRate, anchorName).value)}</td>}
                {showWeightsDims && (
                  <td data-label="Weights & Dims" style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <ProgressDot {...weightsDimsState(item)} onClick={() => navigate(`/products/${item.id}/weights`, { state: { browseIds: rows.map((r) => r.id) } })} />
                  </td>
                )}
                {showQuickbooks && (
                  <td data-label="QuickBooks Item" style={{ padding: '6px 8px', fontSize: 13 }}>
                    {item.quickbooks_link ? (
                      <>
                        <div>{item.quickbooks_link.qbo_item_name}</div>
                        <QuickBooksItemStatusPill status={item.quickbooks_link.qbo_item_status} />
                      </>
                    ) : <span style={{ color: '#888' }}>Not linked</span>}
                  </td>
                )}
                <td className="cell-actions" style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <RowActionsMenu
                    items={[
                      { key: 'edit', label: 'Edit', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleEdit(item) },
                      { key: 'uom-conversions', label: 'Product UoMs', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/products/${item.id}/uom`, { state: { browseIds: rows.map((r) => r.id) } }) },
                      { key: 'product-prices', label: 'Sales Prices', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/sales-prices?product_id=${item.id}`) },
                      { key: 'cost-price', label: 'Cost Price', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/products/${item.id}/cost-prices`) },
                      { key: 'product-media', label: 'Product Media', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/products/${item.id}/media`, { state: { browseIds: rows.map((r) => r.id) } }) },
                      { key: 'product-weights', label: 'Weights & Dims', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => navigate(`/products/${item.id}/weights`, { state: { browseIds: rows.map((r) => r.id) } }) },
                      { key: 'report', label: 'Print', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReport(item) },
                      { key: 'duplicate', label: 'Duplicate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDuplicate(item) },
                      ...(item.is_active
                        ? [{ key: 'deactivate', label: 'Deactivate', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDeactivate(item) }]
                        : [
                            { key: 'reactivate', label: 'Reactivate', disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleReactivate(item) },
                            { key: 'delete', label: 'Delete', danger: true, disabled: showForm, disabledTitle: showForm ? 'Finish editing first' : undefined, onClick: () => handleDelete(item) },
                          ]),
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Products"
        rightContent={(
          <select className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ProductTable;
